// ============================================================================
// API LOGGER UTILITY
// ============================================================================
// Handles logging of API requests and responses

import { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { apiConfig } from '../config/apiConfig';

/**
 * Colors for console logging
 */
const COLORS = {
  request: '#4CAF50',
  response: '#2196F3',
  error: '#F44336',
  warning: '#FF9800',
};

/**
 * Log API Request
 */
export const logRequest = (config: AxiosRequestConfig): void => {
  if (!apiConfig.enableLogging) return;

  console.group(`%c API REQUEST → ${config.method?.toUpperCase()} ${config.url}`, `color: ${COLORS.request}; font-weight: bold;`);
  console.log('⏰ Timestamp:', new Date().toISOString());
  console.log('🔗 Endpoint:', config.url);
  console.log('📋 Method:', config.method?.toUpperCase());
  console.log('📝 Headers:', config.headers);

  if (config.params) {
    console.log('🔍 Query Params:', config.params);
  }

  if (config.data) {
    console.log('📦 Request Body:', config.data);
  }

  console.groupEnd();
};

/**
 * Log API Response
 */
export const logResponse = (response: AxiosResponse): void => {
  if (!apiConfig.enableLogging) return;

  const duration = response.config.headers?.['X-Request-Start-Time']
    ? Date.now() - Number(response.config.headers['X-Request-Start-Time'])
    : 'N/A';

  console.group(`%c API RESPONSE ← ${response.config.method?.toUpperCase()} ${response.config.url}`, `color: ${COLORS.response}; font-weight: bold;`);
  console.log('⏰ Timestamp:', new Date().toISOString());
  console.log('⏱️ Duration:', `${duration}ms`);
  console.log('✅ Status:', response.status, response.statusText);
  console.log('📋 Headers:', response.headers);
  console.log('📦 Response Data:', response.data);
  console.groupEnd();
};

/**
 * Log API Error
 */
export const logError = (error: AxiosError): void => {
  if (!apiConfig.enableLogging) return;

  console.group(`%c API ERROR ✖ ${error.config?.method?.toUpperCase()} ${error.config?.url}`, `color: ${COLORS.error}; font-weight: bold;`);
  console.log('⏰ Timestamp:', new Date().toISOString());
  console.log('🔗 Endpoint:', error.config?.url);
  console.log('📋 Method:', error.config?.method?.toUpperCase());
  console.error('❌ Error Message:', error.message);

  if (error.response) {
    console.log('📊 Status Code:', error.response.status);
    console.log('📦 Error Response:', error.response.data);
    console.log('📋 Response Headers:', error.response.headers);
  } else if (error.request) {
    console.log('📡 No Response Received');
    console.log('📋 Request:', error.request);
  } else {
    console.log('⚙️ Request Setup Error');
  }

  console.log('🔧 Error Config:', error.config);
  console.log('📚 Stack Trace:', error.stack);
  console.groupEnd();
};

/**
 * Log Warning
 */
export const logWarning = (message: string, data?: any): void => {
  if (!apiConfig.enableLogging) return;

  console.group(`%c API WARNING ⚠ ${message}`, `color: ${COLORS.warning}; font-weight: bold;`);
  console.log('⏰ Timestamp:', new Date().toISOString());
  if (data) {
    console.log('📦 Data:', data);
  }
  console.groupEnd();
};

/**
 * Log Info
 */
export const logInfo = (message: string, data?: any): void => {
  if (!apiConfig.enableLogging) return;

  console.log(`%c ℹ️ ${message}`, 'color: #2196F3;');
  if (data) {
    console.log('📦 Data:', data);
  }
};
